import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Radio from './Radio';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { FormInputErrors } from '@pattern-library/form-input';
const optionShape = {
  /**
   * The Radio's ID
   */
  id: PropTypes.string.isRequired,
  /**
   * Text that appears in the Radio
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional custom classes for the Radio
   */
  radioContainerClasses: PropTypes.string,
  /**
   * The value returned by the Radio
   */
  value: PropTypes.string.isRequired
};
const propTypes = {
  /**
   * Additional custom classes for the fieldset element
   */
  className: PropTypes.string,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * Enable border style
   */
  hasBorder: PropTypes.bool,
  /**
   * Enable the error state. Must set `errorMessage` to display message.
   */
  hasError: PropTypes.bool,
  /**
   * Accessibly (visually) hide the legend
   */
  hideLegend: PropTypes.bool,
  /**
   * The RadioGroup's ID
   */
  id: PropTypes.string.isRequired,
  /**
   * Content of the info popover. Enables the popover component when provided.
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the MessagePopover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * The initial radio option value that should be selected on load
   */
  initialValue: PropTypes.string,
  /**
   * Enable the disabled state
   */
  isDisabled: PropTypes.bool,
  /**
   * Radios are stretched evenly to fill space
   */
  isFullWidth: PropTypes.bool,
  /**
   * Enable the required state
   */
  isRequired: PropTypes.bool,
  /**
   * The form's legend
   */
  legend: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional custom classes for the legend element
   */
  legendClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * The HTML name attribute for the radio input elements
   */
  name: PropTypes.string.isRequired,
  /**
   * Function that runs when a radio is clicked.
   * (e: Event) => void
   */
  onChange: PropTypes.func,
  /**
   * Appended to the text label.
   * Defaults to (optional) for non-required inputs, but can be overridden.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * An array of Options objects. Documented Below
   **/
  options: PropTypes.arrayOf(PropTypes.shape(optionShape)).isRequired,
  /**
   * The display orientation of the radio buttons
   */
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
   * `data-qa` attribute value for automated testing
   */
  qaId: PropTypes.string,
  /**
   * Additional custom classes for the radio layout container
   */
  radioLayoutClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * Renders an asterisk next to the legend when the field is required
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Helper text that renders below the radio button group
   */
  subtext: PropTypes.string,
  /**
   * Sets the current value. Can be used to manage the Radiogroup in an externally controlled state or to
   * reset the RadioGroups value to a specific value. Ex. If you want to reset the value you would use `value: ''`
   */
  value: PropTypes.string
};
const defaultProps = {
  className: '',
  errorLevel: 'error',
  errorMessage: '',
  hasBorder: false,
  hasError: false,
  hideLegend: false,
  id: '',
  infoPopoverContent: null,
  infoPopoverProps: {},
  initialValue: '',
  isDisabled: false,
  isFullWidth: false,
  isRequired: false,
  legend: '',
  legendClasses: '',
  onChange: null,
  optionalInputLabel: '(optional)',
  options: [],
  orientation: 'horizontal',
  qaId: '',
  radioLayoutClasses: '',
  showRequiredAsterisk: false,
  subtext: ''
};

// all props are defined here, whether they are used or not,
// in order to prevent them from being passed to the input through radioGroupPassthrough
const RadioGroup = _ref => {
  let {
    className,
    errorLevel,
    errorMessage,
    hasBorder,
    hasError,
    hideLegend,
    id,
    infoPopoverContent,
    infoPopoverProps,
    initialValue,
    // eslint-disable-line no-unused-vars
    isDisabled,
    isFullWidth,
    isRequired,
    legend,
    legendClasses,
    name,
    onChange,
    optionalInputLabel,
    options,
    orientation,
    qaId,
    radioLayoutClasses,
    showRequiredAsterisk,
    subtext,
    value,
    ...radioGroupPassthrough
  } = _ref;
  const [val, setVal] = useState('');
  const hasInfoPopover = infoPopoverContent !== null;
  const ids = {
    fieldSet: [`${id}-radio-group-fieldset`],
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  const handleOnChange = event => {
    if (onChange) {
      onChange(event);
    }
    if (event.target.value !== val) {
      setVal(event.target.value);
    }
  };

  // Set the default value when the component first mounts
  useEffect(() => {
    const firstValue = options.find(option => option.value === initialValue)?.value;
    if (firstValue) {
      setVal(firstValue);
    }
  }, [initialValue, options]);

  // Update the components value if the external value changes
  useEffect(() => {
    const updatedValue = options.find(option => option.value === value)?.value;
    if (updatedValue) {
      setVal(updatedValue);
    } else if (value === '') {
      setVal('');
    }
  }, [value, options]);
  return /*#__PURE__*/React.createElement("fieldset", {
    id: ids.fieldSet[0],
    "data-qa": qaId,
    className: classNames('asc-form-group', 'asc-form-group--radio', className, {
      'is-disabled': isDisabled,
      'asc-form-input--error': hasError && errorMessage && errorLevel === 'error',
      'asc-form-input--warning': hasError && errorMessage && errorLevel === 'warning',
      'asc-form-group--radio-vertical': orientation === 'vertical'
    })
  }, /*#__PURE__*/React.createElement("legend", {
    className: classNames('asc-form-label asc-ptb-0', {
      'asc-acc-hidden': hideLegend,
      legendClasses
    })
  }, legend, isRequired && showRequiredAsterisk && /*#__PURE__*/React.createElement("span", {
    className: "asc-form-required-asterisk"
  }, "\xA0*"), !isRequired && optionalInputLabel && /*#__PURE__*/React.createElement("span", null, "\xA0", optionalInputLabel), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  })), /*#__PURE__*/React.createElement("div", _extends({
    className: "asc-radio-group-error-container",
    role: "radiogroup"
  }, radioGroupPassthrough), /*#__PURE__*/React.createElement("div", {
    className: classNames('asc-radio-group-layout', radioLayoutClasses, {
      'asc-radio-group-layout--full-width': isFullWidth || hasBorder && !isFullWidth
    })
  }, options.map(option => {
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: option.id
    }, /*#__PURE__*/React.createElement(Radio, _extends({}, option, {
      name: name,
      isDisabled: isDisabled,
      isChecked: option.value === val,
      hasBorder: hasBorder,
      onChange: handleOnChange
    })));
  }))), /*#__PURE__*/React.createElement("div", {
    className: "asc-radio-group-message-container"
  }, /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), subtext && /*#__PURE__*/React.createElement("p", {
    id: ids.subtext[0],
    className: "asc-form-subtext asc-m-0 asc-p-0"
  }, subtext)));
};
RadioGroup.defaultProps = defaultProps;
RadioGroup.propTypes = propTypes;
export default RadioGroup;